import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DocumentViewer from './components/DocumentViewer';
import ExtractedDataPanel from './components/ExtractedDataPanel';
import EmailGenerationPanel from './components/EmailGenerationPanel';
import MobileTabNavigation from './components/MobileTabNavigation';
import LogoUploadPanel from './components/LogoUploadPanel';
import NotificationToast from '../../components/ui/NotificationToast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../utils/supabase';

const ContractAnalysisResults = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('results');
  const [notifications, setNotifications] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [dealId, setDealId] = useState(null);
  const notificationTimeouts = useRef({});

  // Check if contract has usable data
  const checkDataQuality = (contractData) => {
    if (!contractData?.extractedData) return false;
    
    const { extractedData } = contractData;
    const extractedTextLength = contractData.extractedTextLength || 0;
    
    // Count how many key fields are missing or "Not specified"
    const keyFields = [
      extractedData.property?.address,
      extractedData.parties?.buyer, 
      extractedData.parties?.seller,
      extractedData.dates?.closingDate?.value,
      extractedData.financial?.purchasePrice || extractedData.property?.purchasePrice
    ];
    
    const emptyFields = keyFields.filter(field => 
      !field || 
      field === 'Not specified' || 
      field === 'N/A' ||
      field === null ||
      field === undefined ||
      field.toString().trim() === ''
    ).length;
    
    // Poor data quality if:
    // 1. Most key fields are empty (3+ out of 5)
    // 2. OR extracted text is too short (under 500 characters)
    const hasPoorQuality = emptyFields >= 3 || extractedTextLength < 500;
    
    console.log('📊 Data Quality Check:', {
      extractedTextLength,
      emptyFields: `${emptyFields}/5`,
      hasPoorQuality,
      keyFieldValues: keyFields
    });
    
    return !hasPoorQuality;
  };

  // Load real contract data from processing
  useEffect(() => {
    const loadContractData = () => {
      try {
        const storedData = localStorage.getItem('latestContractData');
        console.log('🔥 Results Page - Raw localStorage data:', storedData);
        console.log('🔥 Results Page - Current URL:', window.location.href);

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          console.log('🔥 Results Page - Parsed contract data:', JSON.stringify(parsedData, null, 2));
          console.log('🔥 Results Page - extractedData structure:', JSON.stringify(parsedData?.extractedData, null, 2));
          console.log('🔥 Results Page - dates specifically:', JSON.stringify(parsedData?.extractedData?.dates, null, 2));
          console.log('🔥 Results Page - rawGeminiData:', JSON.stringify(parsedData?.rawGeminiData, null, 2));
          
          // Check if this is real processed data or demo data
          const isRealData = parsedData?.extractedData && 
                           parsedData?.extractedData?.property?.address !== "123 Main St, Anytown, CA 90210" &&
                           parsedData?.extractedData?.property?.address !== "Demo Property";
          
          console.log('🔥 Results Page - Is real processed data:', isRealData);
          console.log('🔥 Results Page - Property address:', parsedData?.extractedData?.property?.address);
          
          if (isRealData) {
            console.log('🔥 Results Page - Using real processed data');
            setContractData(parsedData);

            // Set dealId from contract data
            if (parsedData?.dealId) {
              setDealId(parsedData.dealId);
              console.log('🔥 Results Page - Deal ID:', parsedData.dealId);
            }

            // Check data quality and show error popup if needed
            const hasGoodData = checkDataQuality(parsedData);
            if (!hasGoodData) {
              console.warn('⚠️ Poor data quality detected - showing error popup');
              addNotification({
                type: 'error',
                title: '📄 Document Reading Issue',
                message: `We couldn't extract clear data from this PDF. It may be scanned or image-based.

SOLUTION: Convert to searchable text first:
• PDF24 OCR Tool: tools.pdf24.org/en/ocr-pdf
• Google Drive: Upload → Right-click → "Open with Google Docs"

Then reupload the converted file for better results.`,
                duration: 0, // Stay up until manually dismissed (no progress bar)
                action: {
                  label: 'Upload New File',
                  onClick: () => navigate('/')
                }
              });
            }
          } else {
            console.log('🔥 Results Page - Stored data appears to be demo data, falling back to demo display');
            setContractData(parsedData); // Still use it but log that it's demo data
            if (parsedData?.dealId) {
              setDealId(parsedData.dealId);
            }
          }
          
          // Keep the stored data for email generation and other features
          // localStorage.removeItem('latestContractData');
        } else {
          // No contract data found - redirect to upload page
          console.log('🔥 Results Page - No contract data found, redirecting to upload');
          addNotification({
            type: 'warning',
            title: 'No Contract Data',
            message: 'No contract analysis data found. Please upload and process a contract first.',
            duration: 5000
          });
          navigate('/');
        }
      } catch (error) {
        console.error('Error loading contract data:', error);
        addNotification({
          type: 'error',
          title: 'Data Loading Error',
          message: 'Failed to load contract data',
          duration: 5000
        });
      }
    };

    loadContractData();
  }, []);

  // Fix notification system - prevent transparent/stuck notifications
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      duration: notification.duration !== undefined ? notification.duration : 4000
    };
    
    setNotifications(prev => [newNotification, ...prev.slice(0, 2)]); // Max 3 notifications
    
    // Auto-remove notification
    if (newNotification.duration > 0) {
      notificationTimeouts.current[newNotification.id] = setTimeout(() => {
        removeNotification(newNotification.id);
      }, newNotification.duration);
    }
  };

  const removeNotification = (id) => {
    // Clear timeout
    if (notificationTimeouts.current[id]) {
      clearTimeout(notificationTimeouts.current[id]);
      delete notificationTimeouts.current[id];
    }
    
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(notificationTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Handle logo upload
  const handleLogoUpload = async (file) => {
    if (!dealId) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Deal ID not found. Please reload the page.',
        duration: 3000
      });
      return;
    }

    setLogoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dealId', dealId);

      const response = await fetch('/.netlify/functions/upload-logo', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload logo');
      }

      setLogoUrl(data.logoUrl);
      addNotification({
        type: 'success',
        title: 'Logo Uploaded',
        message: 'Your logo has been added to the shared link',
        duration: 3000
      });
    } catch (error) {
      console.error('Logo upload error:', error);
      addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: error.message || 'Failed to upload logo',
        duration: 3000
      });
      throw error;
    } finally {
      setLogoLoading(false);
    }
  };

  // Handle logo remove
  const handleLogoRemove = async () => {
    if (!dealId) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Deal ID not found. Please reload the page.',
        duration: 3000
      });
      return;
    }

    setLogoLoading(true);
    try {
      const { error } = await supabase
        .from('deals')
        .update({ logo_url: null })
        .eq('id', dealId);

      if (error) throw error;

      setLogoUrl(null);
      addNotification({
        type: 'success',
        title: 'Logo Removed',
        message: 'Your logo has been removed',
        duration: 3000
      });
    } catch (error) {
      console.error('Logo remove error:', error);
      addNotification({
        type: 'error',
        title: 'Remove Failed',
        message: 'Failed to remove logo',
        duration: 3000
      });
      throw error;
    } finally {
      setLogoLoading(false);
    }
  };

  const handleHighlightClick = (highlight) => {
    setActiveTab('results');
    addNotification({
      type: 'info',
      title: 'Section Selected',
      message: `Now viewing: ${highlight.section}`,
      duration: 2000
    });
  };

  const handleViewDocument = () => {
    // Try to access stored file data using the contract filename
    const filename = contractData?.contract_filename;
    const storedFileData = localStorage.getItem(`contract_file_${filename}`);
    
    if (storedFileData) {
      try {
        // Create a download link from the base64 data
        const link = document.createElement('a');
        link.href = storedFileData;
        link.download = filename || 'contract.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        addNotification({
          type: 'success',
          title: 'Document Downloaded',
          message: 'Contract PDF downloaded successfully',
          duration: 3000
        });
      } catch (error) {
        console.error('Download error:', error);
        addNotification({
          type: 'error',
          title: 'Download Failed',
          message: 'Unable to download original document',
          duration: 3000
        });
      }
    } else {
      addNotification({
        type: 'info',
        title: 'Document Not Available',
        message: 'Original PDF file not found. Upload a new contract to enable PDF download.',
        duration: 4000
      });
    }
  };

  const handleGenerateCalendar = (type, dateKey, dateInfo) => {
    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      
      if (type === 'ics') {
        addNotification({
          type: 'success',
          title: 'Calendar Downloaded',
          message: 'All important dates saved to your calendar.',
          duration: 3000
        });
      } else {
        addNotification({
          type: 'success',
          title: 'Event Created',
          message: `${type === 'google' ? 'Google' : 'Outlook'} Calendar updated.`,
          duration: 3000
        });
      }
    }, 1500);
  };

  const handleEditDate = (dateKey, dateInfo) => {
    addNotification({
      type: 'info',
      title: 'Edit Date',
      message: `Date editor opening soon for ${dateKey}.`,
      duration: 2000
    });
  };

  const handleCopyEmail = () => {
    addNotification({
      type: 'success',
      title: 'Email Copied',
      message: 'Content copied to clipboard.',
      duration: 2000
    });
  };

  const handleDownloadICS = () => {
    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      addNotification({
        type: 'success',
        title: 'Calendar Downloaded',
        message: 'All dates saved successfully.',
        duration: 3000
      });
    }, 1000);
  };

  const handleNewAnalysis = () => {
    navigate('/contract-upload');
  };

  const handleViewHistory = () => {
    navigate('/contract-history');
  };

  // Render loading state if no contract data
  if (!contractData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin mb-4">
              <Icon name="Loader2" size={32} color="var(--color-primary)" />
            </div>
            <p className="text-muted-foreground">Loading contract data...</p>
          </div>
        </main>
      </div>
    );
  }

  // Desktop layout with single column for better readability
  const renderDesktopLayout = () => (
    <div className="flex-1 overflow-auto p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 bg-muted/30 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('results')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'results'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            Contract Summary
          </button>
          <button
            onClick={() => setActiveTab('document')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'document'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            View PDF
          </button>
          <button
            onClick={() => setActiveTab('logo')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'logo'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            Brand Logo
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'results' ? (
          /* Contract Summary */
          <div className="professional-card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Contract Summary</h3>
                  <p className="text-sm text-muted-foreground">
                    AI-extracted information
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleNewAnalysis}
                    className="px-3 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                  >
                    New Analysis
                  </button>
                  <button
                    onClick={handleViewHistory}
                    className="px-3 py-1 text-xs bg-muted text-muted-foreground rounded hover:bg-muted/80 transition-colors"
                  >
                    History
                  </button>
                </div>
              </div>
            </div>
            <ExtractedDataPanel
              contractData={contractData}
              onGenerateCalendar={handleGenerateCalendar}
              onEditDate={handleEditDate}
            />
          </div>
        ) : activeTab === 'document' ? (
          /* Document Viewer */
          <div className="professional-card h-[800px]">
            <DocumentViewer
              contractData={contractData}
              onHighlightClick={handleHighlightClick}
            />
          </div>
        ) : (
          /* Logo Upload */
          <LogoUploadPanel
            dealId={dealId}
            currentLogo={logoUrl}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
            isLoading={logoLoading}
          />
        )}
      </div>
    </div>
  );

  // Simplified tablet layout
  const renderTabletLayout = () => (
    <div className="flex-1 overflow-auto p-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="professional-card p-4 text-center">
          <Icon name="FileText" size={32} color="var(--color-primary)" className="mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">
            {contractData.contract_filename}
          </p>
          <p className="text-xs text-muted-foreground">
            Analysis Complete
          </p>
        </div>

        <div className="professional-card">
          <ExtractedDataPanel
            contractData={contractData}
            onGenerateCalendar={handleGenerateCalendar}
            onEditDate={handleEditDate}
          />
        </div>
      </div>
    </div>
  );

  // Simplified mobile layout
  const renderMobileLayout = () => {
    const renderTabContent = () => {
      switch (activeTab) {
        case 'document':
          return (
            <div className="flex-1 p-4">
              <div className="professional-card h-full">
                <DocumentViewer onHighlightClick={handleHighlightClick} contractData={contractData} />
              </div>
            </div>
          );
        case 'results':
          return (
            <div className="flex-1 p-4">
              <div className="professional-card h-full">
                <ExtractedDataPanel
                  contractData={contractData}
                  onGenerateCalendar={handleGenerateCalendar}
                  onEditDate={handleEditDate}
                />
              </div>
            </div>
          );
        case 'email':
          return (
            <div className="flex-1 p-4 pb-20">
              <div className="professional-card h-full">
                <div className="p-4 h-full overflow-auto">
                  <EmailGenerationPanel
                    contractData={contractData}
                    onCopyEmail={handleCopyEmail}
                    onDownloadICS={handleDownloadICS}
                  />
                </div>
              </div>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <>
        <div className="flex-1 flex flex-col overflow-hidden">
          {renderTabContent()}
        </div>
        <MobileTabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 h-screen flex flex-col">
        {/* Updated Page Header with real data */}
        <div className="flex-shrink-0 mx-4 mt-4">
          <div className="professional-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Analysis Complete
                </h1>
                <p className="text-muted-foreground">
                  {contractData.extractedData?.summary || 'Your contract has been analyzed successfully'}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => setActiveTab('document')}
                >
                  View PDF
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="History"
                  iconPosition="left"
                  onClick={handleViewHistory}
                >
                  History
                </Button>
                
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleNewAnalysis}
                >
                  New Analysis
                </Button>
              </div>
            </div>

            <div className="mt-3 flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="font-medium text-primary">Ready to Review</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} color="var(--color-muted-foreground)" />
                <button
                  onClick={handleViewDocument}
                  className="text-muted-foreground hover:text-primary transition-colors cursor-pointer underline"
                >
                  {contractData.contract_filename}
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
                <span className="font-medium text-primary">
                  Analysis Complete
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Content */}
        <div className="flex-1">
          <div className="max-w-7xl mx-auto h-full">
            {/* Desktop Layout */}
            <div className="hidden lg:flex h-full">
              {renderDesktopLayout()}
            </div>

            {/* Tablet Layout */}
            <div className="hidden md:flex lg:hidden h-full">
              {renderTabletLayout()}
            </div>

            {/* Mobile Layout */}
            <div className="flex md:hidden h-full">
              {renderMobileLayout()}
            </div>
          </div>
        </div>
      </main>

      {/* Fixed Notifications */}
      <NotificationToast
        notifications={notifications}
        onDismiss={removeNotification}
        maxVisible={3}
      />

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="professional-card p-6 max-w-sm mx-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin">
                <Icon name="Loader2" size={20} color="var(--color-primary)" />
              </div>
              <span className="font-medium text-foreground">Processing...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractAnalysisResults;